import { useRef, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import styles from '@components/MainHeader/MainHeader.styles';
import { CartButton } from '@components/CartButton';
import { CtaButton } from '@components/CtaButton';
import { IMainHeaderBlock } from '@features/cms/typescript';
import { TImage, TVideo } from '@components/typescript';
import RenderMedia from '@utils/RenderMedia';
import SpacingBlock from '@features/cms/components/collections/SpacingBlock/SpacingBlock';
import theme from '@styles/theme';
import { IProductObject } from '@features/categories/typescript';
type PropsType = {
  data: IMainHeaderBlock;
};
const MainHeader = ({
  data
}: PropsType) => {
  const {
    title = '',
    subtitle = '',
    button = {
      text: '',
      link: '',
      style: 'solidGraphite',
      target: '_self'
    },
    backgroundImage,
    backgroundVideo,
    thumbnailsWithBlur,
    marginTop,
    marginBottom,
    background,
    products
  } = data;
  const hasSubtitle = Boolean(subtitle?.trim());
  const spacingBg = background || theme.rawColors.neutralWhite;
  const textContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [scrollY, setScrollY] = useState(0);

  // Parallax scroll effect
  const handleScroll = useCallback(() => {
    if (headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      // Only apply parallax when header is visible
      if (rect.bottom > 0) {
        setScrollY(window.scrollY);
      }
    }
  }, []);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  return <>
      {Number(marginTop) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginTop)),
      code: 'mt'
    }} />}
      <header sx={styles.container} ref={headerRef} data-testid="MainHeader">
        <div sx={styles.wrapper}>
          {/* Background media - static */}
          <div sx={styles.mediaContainer} data-testid="MainHeader-mediaContainer">
            {backgroundImage && RenderMedia(backgroundImage, backgroundVideo || undefined, true, thumbnailsWithBlur, title || '', true)}
          </div>

          {/* Text content - follows scroll with parallax */}
          <div sx={{
          ...styles.textContainer,
          ...styles.parallaxText(scrollY)
        }} ref={textContainerRef} data-testid="MainHeader-textContainer">
            {title && <h1 sx={{
            ...styles.title,
            ...(!hasSubtitle ? styles.titleWithoutSubtitle : {})
          }}>{title}</h1>}

            {subtitle && <p sx={styles.subtitle}>{subtitle}</p>}

            {button?.text && button?.link && <CtaButton text={button.text} href={button.link} />}
          </div>

          {products && products.length > 0 && <CartButton image={{
          image: backgroundImage as TImage
        }} video={backgroundVideo as TVideo} products={products as IProductObject[]} />}
        </div>
      </header>

      {Number(marginBottom) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginBottom)),
      code: 'mb'
    }} />}
    </>;
};
export default observer(MainHeader);